import React, { useEffect, useState, useRef, useCallback } from 'react';
import { getSamples, getNextSampleId, analyzeSamplePhotos, createSampleV2, updateSample, deleteSample, uploadSamplePhoto, getMasterData } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { MasterDataAutocomplete } from '../components/MasterDataAutocomplete';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Beaker, Search, Camera, Upload, Sparkles, X, Loader2, Check, AlertCircle, Building2, Download } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import ExportDialog from '../components/ExportDialog';
import useExportShortcut from '../hooks/useExportShortcut';
import { useScreenState, useScrollRestoration } from '../hooks/useScreenState';
import { exportToExcel, exportToPDF, EXPORT_COLUMNS } from '../utils/exportUtils';

export default function SampleMaster() {
  const [samples, setSamples] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useScreenState('sampleMaster.searchQuery', '');

  useScrollRestoration('sampleMaster', !loading);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSample, setEditingSample] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, sampleId: '', sampleName: '' });
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  
  // New sample workflow state
  const [step, setStep] = useState(1); // 1: Photos, 2: Analyze, 3: Review
  const [photos, setPhotos] = useState([]); // Base64 for preview
  const [photoPaths, setPhotoPaths] = useState([]); // File paths from server
  const [photoFiles, setPhotoFiles] = useState([]); // Actual files to upload
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiExtractedValues, setAiExtractedValues] = useState(null);
  const [supplierMatches, setSupplierMatches] = useState([]); // Top supplier matches from AI
  const [nextSampleId, setNextSampleId] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  
  // Form values
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [formValues, setFormValues] = useState({
    trade_name: '',
    design_name: '',
    gsm: '',
    width: '',
    composition: '',
    color: '',
    remarks: ''
  });
  
  // Track supplier detection
  const [supplierDetection, setSupplierDetection] = useState(null);
  const [editedFields, setEditedFields] = useState(new Set());
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // ALT+E shortcut for export
  useExportShortcut(() => setExportDialogOpen(true));

  // Export handler - uses filteredSamples defined below
  const handleExport = async (format, scope) => {
    // Get filtered samples for export
    const query = searchQuery.toLowerCase();
    const exportFilteredSamples = samples.filter(sample => 
      (sample.sample_id || '').toLowerCase().includes(query) ||
      (sample.supplier_name || '').toLowerCase().includes(query) ||
      (sample.trade_name || '').toLowerCase().includes(query) ||
      (sample.design_name || '').toLowerCase().includes(query)
    );
    
    const dataToExport = scope === 'current' ? exportFilteredSamples : samples;
    
    if (dataToExport.length === 0) {
      return { success: false, message: 'No data available to export.' };
    }

    const filterDesc = searchQuery ? [`Search: ${searchQuery}`] : [];

    if (format === 'excel') {
      return exportToExcel(dataToExport, EXPORT_COLUMNS.samples, 'Samples', 'Samples');
    } else {
      return exportToPDF(dataToExport, EXPORT_COLUMNS.samples, 'Samples', 'Sample Master Report', filterDesc);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [samplesRes, suppliersRes] = await Promise.all([
        getSamples(),
        getMasterData('supplier')
      ]);
      setSamples(samplesRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load sample data');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = async () => {
    setEditingSample(null);
    setStep(1);
    setPhotos([]);
    setPhotoPaths([]);
    setPhotoFiles([]);
    setAiExtractedValues(null);
    setSupplierMatches([]);
    setSelectedSupplierId('');
    setSupplierDetection(null);
    setFormValues({
      trade_name: '',
      design_name: '',
      gsm: '',
      width: '',
      composition: '',
      color: '',
      remarks: ''
    });
    setEditedFields(new Set());
    
    try {
      const res = await getNextSampleId();
      setNextSampleId(res.data.sample_id);
    } catch (error) {
      console.error('Failed to get next sample ID:', error);
      setNextSampleId('SMP-XXXX');
    }
    
    setDialogOpen(true);
  };

  const openEditDialog = (sample) => {
    setEditingSample(sample);
    setStep(3);
    setPhotos(sample.photo_paths || sample.photos || []);
    setPhotoPaths(sample.photo_paths || []);
    setPhotoFiles([]);
    setAiExtractedValues(sample.ai_extracted_values || null);
    setSupplierMatches([]);
    setNextSampleId(sample.sample_id);
    setSelectedSupplierId(sample.supplier_id || '');
    setSupplierDetection(sample.supplier_detection || null);
    setFormValues({
      trade_name: sample.trade_name || '',
      design_name: sample.design_name || '',
      gsm: sample.gsm || '',
      width: sample.width || '',
      composition: sample.composition || '',
      color: sample.color || '',
      remarks: sample.remarks || ''
    });
    setEditedFields(new Set());
    setDialogOpen(true);
  };

  const handleFileSelect = useCallback((event) => {
    const files = Array.from(event.target.files);
    
    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select image files only');
        return;
      }
      
      // Store file for later upload
      setPhotoFiles(prev => [...prev, file]);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotos(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
    
    event.target.value = '';
  }, []);

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoFiles(prev => prev.filter((_, i) => i !== index));
    setPhotoPaths(prev => prev.filter((_, i) => i !== index));
  };

  const uploadPhotos = async () => {
    if (photoFiles.length === 0) return [];
    
    setUploading(true);
    const uploadedPaths = [];
    
    try {
      for (const file of photoFiles) {
        const response = await uploadSamplePhoto(file);
        uploadedPaths.push(response.data.photo_url);
      }
      setPhotoPaths(uploadedPaths);
      return uploadedPaths;
    } catch (error) {
      console.error('Failed to upload photos:', error);
      toast.error('Failed to upload photos');
      return [];
    } finally {
      setUploading(false);
    }
  };

  const handleAnalyze = async () => {
    if (photos.length === 0) {
      toast.error('Please add at least one photo first');
      return;
    }
    
    setAnalyzing(true);
    try {
      // First upload photos to get paths
      const paths = await uploadPhotos();
      
      // Analyze with base64 photos
      const response = await analyzeSamplePhotos(photos, paths);
      const { extracted_values, supplier_matches, raw_text } = response.data;
      
      setAiExtractedValues(extracted_values);
      setSupplierMatches(supplier_matches || []);
      
      // Set form values from extraction
      setFormValues({
        trade_name: extracted_values.trade_name || '',
        design_name: extracted_values.design_name || '',
        gsm: extracted_values.gsm || '',
        width: extracted_values.width || '',
        composition: extracted_values.composition || '',
        color: extracted_values.color || '',
        remarks: extracted_values.remarks || ''
      });
      
      // Auto-select top supplier match if confidence > 0.7
      if (supplier_matches && supplier_matches.length > 0) {
        const topMatch = supplier_matches[0];
        if (topMatch.confidence >= 0.7) {
          setSelectedSupplierId(topMatch.supplier_id);
        }
        
        // Store detection for training
        setSupplierDetection({
          ai_suggested_supplier_id: topMatch.supplier_id,
          ai_suggested_supplier_name: topMatch.supplier_name,
          ai_confidence: topMatch.confidence,
          match_reasons: topMatch.match_reasons || [],
          alternative_matches: supplier_matches.slice(1),
          raw_ocr_text: raw_text || ''
        });
      }
      
      setEditedFields(new Set());
      setStep(3);
      toast.success('Analysis complete! Review the extracted values.');
    } catch (error) {
      console.error('Analysis failed:', error);
      toast.error(error.response?.data?.detail || 'Analysis failed. Please fill in values manually.');
      setStep(3);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
    
    if (aiExtractedValues) {
      const aiValue = aiExtractedValues[field] || '';
      if (value !== aiValue) {
        setEditedFields(prev => new Set([...prev, field]));
      } else {
        setEditedFields(prev => {
          const newSet = new Set(prev);
          newSet.delete(field);
          return newSet;
        });
      }
    }
  };

  const handleSupplierChange = (supplierId) => {
    setSelectedSupplierId(supplierId);
    
    // Track if supplier was changed from AI suggestion
    if (supplierDetection && supplierId !== supplierDetection.ai_suggested_supplier_id) {
      setEditedFields(prev => new Set([...prev, 'supplier']));
    }
  };

  const handleSave = async () => {
    // Validation - supplier is required
    if (!selectedSupplierId) {
      toast.error('Please select a supplier from Supplier Master');
      return;
    }

    setFormLoading(true);
    try {
      if (editingSample) {
        // Update existing sample
        const selectedSupplier = suppliers.find(s => s.id === selectedSupplierId);
        const updateData = {
          supplier_id: selectedSupplierId,
          supplier_name: selectedSupplier?.name || '',
          ...formValues,
          photo_paths: photoPaths.length > 0 ? photoPaths : (editingSample.photo_paths || [])
        };
        const response = await updateSample(editingSample.id, updateData);
        setSamples(prev => prev.map(s => s.id === editingSample.id ? response.data : s));
        toast.success('Sample updated successfully');
      } else {
        // Create new sample with v2 API
        const fieldCorrections = [];
        
        // Build corrections array
        if (aiExtractedValues) {
          Object.keys(formValues).forEach(field => {
            const aiValue = aiExtractedValues[field] || '';
            const finalValue = formValues[field] || '';
            if (editedFields.has(field) || aiValue !== finalValue) {
              fieldCorrections.push({
                field_name: field,
                ai_value: aiValue,
                final_value: finalValue,
                was_corrected: editedFields.has(field)
              });
            }
          });
        }
        
        // Update supplier detection with final selection
        const selectedSupplier = suppliers.find(s => s.id === selectedSupplierId);
        const finalSupplierDetection = supplierDetection ? {
          ...supplierDetection,
          final_supplier_id: selectedSupplierId,
          final_supplier_name: selectedSupplier?.name || '',
          was_changed: supplierDetection.ai_suggested_supplier_id !== selectedSupplierId
        } : null;
        
        const createData = {
          photo_paths: photoPaths,
          supplier_id: selectedSupplierId,
          ai_extracted_values: aiExtractedValues,
          supplier_detection: finalSupplierDetection,
          final_values: formValues,
          field_corrections: fieldCorrections
        };
        
        const response = await createSampleV2(createData);
        setSamples(prev => [response.data, ...prev]);
        toast.success('Sample added successfully');
      }
      setDialogOpen(false);
    } catch (error) {
      console.error('Failed to save sample:', error);
      toast.error(error.response?.data?.detail || 'Failed to save sample');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteClick = (sample) => {
    setDeleteDialog({ open: true, sampleId: sample.id, sampleName: sample.sample_id });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteSample(deleteDialog.sampleId);
      setSamples(prev => prev.filter(s => s.id !== deleteDialog.sampleId));
      toast.success('Sample deleted successfully');
    } catch (error) {
      console.error('Failed to delete sample:', error);
      toast.error('Failed to delete sample');
    } finally {
      setDeleteDialog({ open: false, sampleId: '', sampleName: '' });
    }
  };

  const filteredSamples = samples.filter(sample => {
    const query = searchQuery.toLowerCase();
    return (
      (sample.sample_id || '').toLowerCase().includes(query) ||
      (sample.supplier_name || '').toLowerCase().includes(query) ||
      (sample.trade_name || '').toLowerCase().includes(query) ||
      (sample.design_name || '').toLowerCase().includes(query)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Get supplier name by ID
  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier?.name || '';
  };

  // Render field with correction indicator
  const renderField = (fieldName, label, placeholder = '') => {
    const aiValue = aiExtractedValues?.[fieldName] || '';
    const currentValue = formValues[fieldName] || '';
    const wasEdited = editedFields.has(fieldName);
    const hasAiValue = aiExtractedValues && aiValue;
    
    return (
      <div>
        <Label className="text-maroon-700 flex items-center gap-2">
          {label}
          {hasAiValue && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${wasEdited ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
              {wasEdited ? 'Edited' : 'AI'}
            </span>
          )}
        </Label>
        <Input
          value={currentValue}
          onChange={(e) => handleFieldChange(fieldName, e.target.value)}
          placeholder={placeholder || `Enter ${label.toLowerCase()}...`}
          className={`mt-1 ${wasEdited ? 'border-amber-400' : hasAiValue ? 'border-emerald-400' : ''}`}
        />
        {hasAiValue && wasEdited && aiValue !== currentValue && (
          <p className="text-xs text-maroon-500 mt-1">
            AI detected: &ldquo;{aiValue}&rdquo;
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-maroon-600">Loading samples...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sample-master-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight text-maroon-950">Sample Master</h1>
          <p className="text-maroon-700 mt-1 text-sm lg:text-base">
            AI-assisted fabric sample entry and management
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setExportDialogOpen(true)}
            className="border-maroon-300 text-maroon-700 hover:bg-maroon-50"
            data-testid="export-button"
            title="Export (Alt+E)"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button
            data-testid="add-sample-button"
            onClick={openAddDialog}
            className="bg-maroon-900 hover:bg-maroon-800"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Sample
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-maroon-400" />
        <Input
          data-testid="search-samples-input"
          placeholder="Search by Sample ID, Supplier, Trade Name, or Design..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Samples List */}
      <Card className="border-maroon-200 border-l-4 border-l-violet-500">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg lg:text-xl font-semibold text-maroon-950 flex items-center gap-2">
            <Beaker className="h-5 w-5 text-violet-600" />
            Samples ({filteredSamples.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredSamples.length === 0 ? (
            <div className="text-center py-12">
              <Beaker className="h-12 w-12 mx-auto text-maroon-300 mb-3" />
              <p className="text-maroon-600">
                {searchQuery ? 'No samples match your search' : 'No samples added yet'}
              </p>
              <p className="text-maroon-400 text-sm mt-1">
                {searchQuery ? 'Try a different search term' : 'Click "Add Sample" to get started with AI-assisted entry'}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-maroon-50 border-b border-maroon-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-maroon-800 uppercase">Sample ID</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-maroon-800 uppercase">Supplier</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-maroon-800 uppercase">Trade Name</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-maroon-800 uppercase">Design</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-maroon-800 uppercase">GSM</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-maroon-800 uppercase">Date</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-maroon-800 uppercase w-20">Photos</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-maroon-800 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSamples.map((sample) => (
                      <tr key={sample.id} className="border-b border-maroon-100 hover:bg-maroon-50/50">
                        <td className="py-3 px-4">
                          <span className="font-medium text-maroon-950">{sample.sample_id}</span>
                        </td>
                        <td className="py-3 px-4 text-maroon-700">
                          <div className="flex items-center gap-1">
                            <Building2 className="h-3 w-3 text-maroon-400" />
                            {sample.supplier_name || '-'}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-maroon-700">{sample.trade_name || '-'}</td>
                        <td className="py-3 px-4 text-maroon-600">{sample.design_name || '-'}</td>
                        <td className="py-3 px-4 text-maroon-600">{sample.gsm || '-'}</td>
                        <td className="py-3 px-4 text-maroon-600">{formatDate(sample.date_received)}</td>
                        <td className="py-3 px-4 text-center">
                          {(sample.photo_paths?.length > 0 || sample.photos?.length > 0) ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600">
                              <Camera className="h-4 w-4" />
                              {sample.photo_paths?.length || sample.photos?.length}
                            </span>
                          ) : (
                            <span className="text-maroon-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              data-testid={`edit-sample-${sample.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(sample)}
                              className="h-8 w-8 p-0 text-maroon-600 hover:bg-maroon-100"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              data-testid={`delete-sample-${sample.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(sample)}
                              className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {filteredSamples.map((sample) => (
                  <div
                    key={sample.id}
                    className="border border-maroon-200 rounded-lg bg-white p-4 space-y-2"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="font-semibold text-maroon-950 text-lg">{sample.sample_id}</span>
                        <p className="text-sm text-maroon-600 flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {sample.supplier_name || 'No supplier'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(sample)}
                          className="h-8 w-8 p-0 text-maroon-600 hover:bg-maroon-100"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(sample)}
                          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-maroon-500">Trade:</span>
                        <span className="ml-1 text-maroon-800">{sample.trade_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-maroon-500">Design:</span>
                        <span className="ml-1 text-maroon-800">{sample.design_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-maroon-500">GSM:</span>
                        <span className="ml-1 text-maroon-800">{sample.gsm || '-'}</span>
                      </div>
                      <div>
                        <span className="text-maroon-500">Photos:</span>
                        <span className="ml-1 text-maroon-800">{sample.photo_paths?.length || sample.photos?.length || 0}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Sample Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-maroon-950 flex items-center gap-2">
              {editingSample ? 'Edit Sample' : 'Add New Sample'}
              {!editingSample && (
                <span className="text-sm font-normal text-maroon-500 bg-maroon-100 px-2 py-0.5 rounded">
                  {nextSampleId}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {/* Step Indicator */}
          {!editingSample && (
            <div className="flex items-center justify-center gap-2 py-2">
              <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${step >= 1 ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-500'}`}>
                <Camera className="h-4 w-4" />
                <span className="hidden sm:inline">Photos</span>
              </div>
              <div className="w-8 h-0.5 bg-maroon-200" />
              <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${step >= 2 ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-500'}`}>
                <Sparkles className="h-4 w-4" />
                <span className="hidden sm:inline">Analyze</span>
              </div>
              <div className="w-8 h-0.5 bg-maroon-200" />
              <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${step >= 3 ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-500'}`}>
                <Check className="h-4 w-4" />
                <span className="hidden sm:inline">Review</span>
              </div>
            </div>
          )}

          <div className="py-4">
            {/* Step 1 & 2: Photos */}
            {(step === 1 || step === 2) && !editingSample && (
              <div className="space-y-4">
                <div className="text-center">
                  <h3 className="font-medium text-maroon-950 mb-2">Upload Sample Photos</h3>
                  <p className="text-sm text-maroon-600">
                    Take photos of the fabric sample label or tag. AI will extract information and identify the supplier.
                  </p>
                </div>
                
                {/* Photo Grid */}
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-maroon-200 group">
                      <img src={photo} alt={`Sample ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square rounded-lg border-2 border-dashed border-maroon-300 hover:border-violet-500 hover:bg-violet-50 flex flex-col items-center justify-center gap-1 text-maroon-500 hover:text-violet-600 transition-colors"
                  >
                    <Upload className="h-6 w-6" />
                    <span className="text-xs">Upload</span>
                  </button>
                  
                  <button
                    onClick={() => cameraInputRef.current?.click()}
                    className="aspect-square rounded-lg border-2 border-dashed border-maroon-300 hover:border-violet-500 hover:bg-violet-50 flex flex-col items-center justify-center gap-1 text-maroon-500 hover:text-violet-600 transition-colors sm:hidden"
                  >
                    <Camera className="h-6 w-6" />
                    <span className="text-xs">Camera</span>
                  </button>
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                {photos.length === 0 && (
                  <div className="text-center py-8 border border-dashed border-maroon-300 rounded-lg bg-maroon-50/50">
                    <Camera className="h-10 w-10 mx-auto text-maroon-300 mb-2" />
                    <p className="text-maroon-500">No photos yet</p>
                    <p className="text-xs text-maroon-400 mt-1">Upload or capture photos of the sample label</p>
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row gap-2 pt-4">
                  <Button
                    onClick={handleAnalyze}
                    disabled={photos.length === 0 || analyzing || uploading}
                    className="flex-1 bg-violet-600 hover:bg-violet-700"
                  >
                    {analyzing || uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {uploading ? 'Uploading...' : 'Analyzing...'}
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Analyze Sample
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setStep(3)}
                    className="border-maroon-300"
                  >
                    Skip Analysis
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Review and Edit Fields */}
            {step === 3 && (
              <div className="space-y-4">
                {/* Supplier Matches Banner */}
                {supplierMatches.length > 0 && !editingSample && (
                  <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="h-5 w-5 text-violet-600" />
                      <span className="font-medium text-violet-800">
                        {supplierMatches[0].confidence >= 0.7 ? 'Supplier Detected' : 'Possible Suppliers'}
                      </span>
                    </div>
                    {supplierMatches.length > 1 && supplierMatches[0].confidence < 0.7 && (
                      <p className="text-xs text-violet-600 mb-2">Multiple matches found. Please select the correct supplier:</p>
                    )}
                    <div className="space-y-1">
                      {supplierMatches.map((match, idx) => (
                        <div 
                          key={match.supplier_id} 
                          className={`flex items-center justify-between p-2 rounded ${idx === 0 && match.confidence >= 0.7 ? 'bg-violet-100' : 'bg-white'}`}
                        >
                          <div>
                            <span className="font-medium text-maroon-950">{match.supplier_name}</span>
                            <span className="ml-2 text-xs text-violet-600">
                              ({Math.round(match.confidence * 100)}% match)
                            </span>
                          </div>
                          {match.match_reasons && match.match_reasons.length > 0 && (
                            <span className="text-xs text-maroon-500">{match.match_reasons[0]}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* AI Status Banner */}
                {aiExtractedValues && !supplierMatches.length && !editingSample && (
                  <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <Sparkles className="h-5 w-5 text-emerald-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-emerald-800">AI Analysis Complete</p>
                      <p className="text-xs text-emerald-600">Review and correct values below. Select supplier from Supplier Master.</p>
                    </div>
                  </div>
                )}
                
                {!aiExtractedValues && !editingSample && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Manual Entry Mode</p>
                      <p className="text-xs text-amber-600">Fill in the sample details. Supplier selection is required.</p>
                    </div>
                  </div>
                )}

                {/* Photo Thumbnails */}
                {photos.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {photos.map((photo, index) => (
                      <div key={index} className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border border-maroon-200">
                        <img src={photo} alt={`Sample ${index + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                    {!editingSample && (
                      <button
                        onClick={() => setStep(1)}
                        className="flex-shrink-0 w-16 h-16 rounded-lg border-2 border-dashed border-maroon-300 flex items-center justify-center text-maroon-400 hover:text-violet-600 hover:border-violet-400"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                )}

                {/* Supplier Selection - Required */}
                <div className="p-3 bg-maroon-50 border border-maroon-200 rounded-lg">
                  <Label className="text-maroon-700 flex items-center gap-2 mb-2">
                    <Building2 className="h-4 w-4" />
                    Supplier <span className="text-red-500">*</span>
                    {supplierMatches.length > 0 && selectedSupplierId && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        selectedSupplierId === supplierMatches[0]?.supplier_id 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {selectedSupplierId === supplierMatches[0]?.supplier_id ? 'AI Match' : 'Changed'}
                      </span>
                    )}
                  </Label>
                  <MasterDataAutocomplete
                    value={selectedSupplierId}
                    onValueChange={handleSupplierChange}
                    options={suppliers.map((supplier) => ({
                      value: supplier.id,
                      label: supplier.name,
                      description: supplier.city || '',
                      keywords: supplier.city || '',
                    }))}
                    placeholder="Select supplier from Supplier Master..."
                    searchPlaceholder="Search suppliers..."
                    emptyMessage="No matching supplier in Supplier Master"
                    inputClassName="bg-white"
                    testId="supplier-select"
                  />
                  {!selectedSupplierId && (
                    <p className="text-xs text-red-500 mt-1">Supplier selection is required to save</p>
                  )}
                </div>

                {/* Form Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {renderField('trade_name', 'Trade Name', 'e.g., Premium Cotton')}
                  {renderField('design_name', 'Design Name', 'e.g., Floral Print 001')}
                  {renderField('gsm', 'GSM', 'e.g., 180')}
                  {renderField('width', 'Width', 'e.g., 44 inches')}
                  {renderField('composition', 'Composition', 'e.g., 100% Cotton')}
                  {renderField('color', 'Color', 'e.g., Navy Blue')}
                </div>
                
                <div>
                  {renderField('remarks', 'Remarks', 'Any additional notes...')}
                </div>

                {/* Correction Summary */}
                {editedFields.size > 0 && !editingSample && (
                  <div className="text-xs text-maroon-500 bg-maroon-50 p-2 rounded">
                    <strong>Corrections made:</strong> {Array.from(editedFields).join(', ')}
                    <br />
                    <span className="text-maroon-400">These corrections will be saved as training data.</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-maroon-300"
            >
              Cancel
            </Button>
            {step === 3 && (
              <Button
                data-testid="save-sample-button"
                onClick={handleSave}
                disabled={formLoading || !selectedSupplierId}
                className="bg-maroon-900 hover:bg-maroon-800"
              >
                {formLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {editingSample ? 'Update Sample' : 'Save Sample'}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog(prev => ({ ...prev, open }))}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-maroon-950">Delete Sample?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete sample <strong>&ldquo;{deleteDialog.sampleName}&rdquo;</strong>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-maroon-300">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        onExport={handleExport}
        title="Export Samples"
        hasFilters={!!searchQuery}
        filterDescription={searchQuery ? `Search: ${searchQuery}` : ''}
        totalRecords={samples.length}
        filteredRecords={filteredSamples.length}
      />
    </div>
  );
}
